// Video URL utilities for handling different video sources

export interface VideoInfo {
  type: 'direct' | 'youtube' | 'vimeo' | 'invalid';
  url: string;
  embedUrl?: string;
  thumbnail?: string;
}

export const processVideoUrl = (url: string): VideoInfo => {
  if (!url) {
    return { type: 'invalid', url: '' };
  }

  const trimmedUrl = url.trim();

  // Handle blob URLs (for uploaded files)
  if (trimmedUrl.startsWith('blob:')) {
    return {
      type: 'direct',
      url: trimmedUrl
    };
  }

  // YouTube URLs
  if (trimmedUrl.includes('youtube.com') || trimmedUrl.includes('youtu.be')) {
    let videoId = '';
    
    // youtube.com/watch?v=VIDEO_ID
    const watchMatch = trimmedUrl.match(/youtube\.com\/watch\?v=([^&]+)/);
    if (watchMatch) {
      videoId = watchMatch[1];
    }
    
    // youtube.com/shorts/VIDEO_ID
    const shortsMatch = trimmedUrl.match(/youtube\.com\/shorts\/([^?]+)/);
    if (shortsMatch) {
      videoId = shortsMatch[1];
    }
    
    // youtu.be/VIDEO_ID
    const shortMatch = trimmedUrl.match(/youtu\.be\/([^?]+)/);
    if (shortMatch) {
      videoId = shortMatch[1];
    }
    
    // youtube.com/embed/VIDEO_ID
    const embedMatch = trimmedUrl.match(/youtube\.com\/embed\/([^?]+)/);
    if (embedMatch) {
      videoId = embedMatch[1];
    }

    if (videoId) {
      return {
        type: 'youtube',
        url: trimmedUrl,
        embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1&rel=0&enablejsapi=1`,
        thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
      };
    }
  }

  // Vimeo URLs
  if (trimmedUrl.includes('vimeo.com')) {
    const vimeoMatch = trimmedUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const videoId = vimeoMatch[1];
      return {
        type: 'vimeo',
        url: trimmedUrl,
        embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1`,
        thumbnail: `https://vumbnail.com/${videoId}.jpg`
      };
    }
  }

  // Direct video files (HTTP/HTTPS URLs)
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    const videoExtensions = /\.(mp4|webm|ogg|mov|avi|mkv)$/i;
    if (videoExtensions.test(trimmedUrl)) {
      return {
        type: 'direct',
        url: trimmedUrl
      };
    }
  }

  return { type: 'invalid', url: trimmedUrl };
};

export const isValidVideoUrl = (url: string): boolean => {
  const videoInfo = processVideoUrl(url);
  return videoInfo.type !== 'invalid';
};

export const getVideoEmbedUrl = (url: string): string | null => {
  const videoInfo = processVideoUrl(url);
  return videoInfo.embedUrl || null;
};

export const getVideoThumbnail = (url: string): string | null => {
  const videoInfo = processVideoUrl(url);
  return videoInfo.thumbnail || null;
}; 